import styles from './styles.scss';
import { scrollToTarget, NO_ELEMENT, rm } from 'utils';
import { reducer, isValid, productChanged, changesInitialState } from './ChangesHelper';
import { ChangeEvent, useEffect, useReducer, useRef } from 'react';
import { useToggle } from 'hooks';
import { postSendChange } from 'services/ApiCustomerSupportFormService';
import { Button, LoaderButton } from 'components/Button';
import ExtendedInfo from 'views/CustomerPage/OrderHistory/ExtendedInfo';
import { DisplayMode } from 'views/CustomerPage/OrderHistory/OrderLine';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer
} from 'components/CustomerSupportForms/Components/SectionContainer/SectionContainer';
import { TextArea } from 'components/Shared/Form';
import { IBasicResponse } from 'models/IBasicResponse';
import MessageView from 'components/CustomerSupportForms/Components/MessageView';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';
import { OrderFormProps, contactOptionChanged } from '../Utils';

const CHANGE_MAX_LENGTH = 1000;

export const ChangesForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);

  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, changesInitialState);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [order, error]);

  if (internalState.hasError || !order?.orderNumber) {
    return (
      <MessageView
        title={rm('CustomerPage_Return_Error_Title')}
        message={rm('CustomerPage_Return_Error_Msg')}
        returnUrl={returnUrl}
      />
    );
  }

  const handleChangeInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'change',
      value: newValue
    });
    internalDispatch({
      type: 'changeError',
      value: ''
    });
  };
  const { address, email, products, contactOption, change } = internalState;

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    if (isValid(internalState, internalDispatch)) {
      if (email && order && address) {
        return postSendChange({
          email,
          orderNumber: order.orderNumber,
          change,
          products: products.filter(p => p.quantity > 0), // Remove those with quantity 0
          hash: order.hash,
          customerContactOption: contactOption,
          address
        })
          .then(() => {
            toggleLoading();
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.Changes));
          })
          .catch((response: IBasicResponse) => {
            toggleLoading();
            internalDispatch({ type: 'generalError', value: response.message[0] });
          });
      }
    }

    toggleLoading();
    return Promise.resolve();
  };

  return (
    <>
      <h3 className={styles.subHeader}>{rm('customerpage_returns_products_header')}</h3>
      {order && (
        <ExtendedInfo
          order={order}
          displayMode={DisplayMode.Change}
          returnProductChanged={p => productChanged(p, internalDispatch)}
          className={styles.products}
        />
      )}
      {internalState.productsError && (
        <BlueSectionContainer error={true}>{internalState.productsError}</BlueSectionContainer>
      )}
      <h3 className={styles.subHeader}>{rm('customerpage_change_why_header')}</h3>
      <BlueSectionContainer error={internalState.changeError}>
        <span>{rm('customerpage_change_why_body')}</span>
        <TextArea
          onChange={handleChangeInput}
          textlimit={CHANGE_MAX_LENGTH}
          value={internalState.change}
        />
        <p id={styles.returnMsgLength}>{`(${internalState.change.length}/${CHANGE_MAX_LENGTH})`}</p>
      </BlueSectionContainer>
      {internalState.changeError && <ErrorContainer>{internalState.changeError}</ErrorContainer>}
      <CustomerContact
        contact={internalState.contactOption}
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
